import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useConcierge } from "@/context/ConciergeContext";
import { GENDERS, PICK_SIZES } from "@/config/sizeCharts";
import "@/components/Concierge.css";

/* ---------------- adaptive question model ---------------- */
const CATEGORIES = [
  { v: "footwear", slug: "footwear", t: "Footwear", d: "Shoes, sneakers, sandals & everyday essentials" },
  { v: "apparel", slug: "apparel", t: "Apparel", d: "Everyday wear, activewear & more" },
  { v: "sports", slug: "sports-equipment", t: "Sports & Outdoors", d: "Training, trekking, fitness & equipment" },
  { v: "unsure", slug: null, t: "Not Sure Yet", d: "Show me a little of everything" },
];
const USE = {
  footwear: { q: "What will you use it for?", opts: ["Everyday", "Work / Formal", "Sports & Training", "Travel & Outdoors", "Casual / Sneakers", "Something Special"] },
  apparel: { q: "How will you wear it most?", opts: ["Everyday", "Work / Office", "Activewear", "Occasion / Going Out", "Layering / Seasonal"] },
  sports: { q: "What are you training for?", opts: ["Gym & Strength", "Running", "Trekking & Hiking", "Team Sports", "Yoga & Mobility", "General Fitness"] },
  unsure: { q: "What's the occasion?", opts: ["Everyday", "Work", "Sport & Active", "Travel", "Just Browsing"] },
};
const PRIORITIES = ["Comfort", "Style", "Performance", "Durability", "Value", "Brand"];
const PRICES = [
  { t: "Under ₹1,000", min: 0, max: 1000 },
  { t: "₹1,000 – ₹2,500", min: 1000, max: 2500 },
  { t: "₹2,500 – ₹5,000", min: 2500, max: 5000 },
  { t: "₹5,000 – ₹10,000", min: 5000, max: 10000 },
  { t: "₹10,000+", min: 10000, max: Infinity },
  { t: "Show me everything", min: 0, max: Infinity, any: true },
];
// Size step, driven by a fixed size chart mapped to category + gender (a standard
// chart, not derived from inconsistent per-product size data).
function step5(cat, gender = "men") {
  if (cat === "footwear") return { kind: "size", genders: true, foot: true, q: "What's your usual shoe size?", opts: PICK_SIZES.footwear[gender] || PICK_SIZES.footwear.men, skip: "Don't know / I'll choose later" };
  if (cat === "apparel") return { kind: "size", genders: true, q: "What's your usual size?", opts: PICK_SIZES.apparel[gender] || PICK_SIZES.apparel.men, skip: "Not sure / I'll choose later" };
  return { kind: "pref", q: "How do you like to shop?", opts: ["Trusted brands", "Best value", "New & emerging makers", "Whatever's certified best"], skip: "No preference" };
}
const catBySlug = (v) => CATEGORIES.find((c) => c.v === v);
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const AUTO_OPEN_DELAY = 3500;
const BLOCK_PREFIXES = ["/admin", "/vendor", "/login", "/register", "/reset-password", "/forgot-password", "/order-confirmed"];

/* ---------------- recommendation scoring (mirrors the brief's priority order) ---------------- */
function scoreProduct(p, profile) {
  const sizes = Array.isArray(p.sizes) ? p.sizes.map(String) : [];
  const size = profile.size;
  const needSize = size && sizes.length > 0;
  let s = 0;
  if (needSize) { if (sizes.includes(String(size))) s += 1000; else s -= 500; }
  // category is applied as a query filter already; give a light relevance nudge
  s += 40;
  const use = (profile.use || "").toLowerCase();
  const hay = [p.title, p.description, p.material, (p.highlights || []).join(" "), Object.values(p.attributes || {}).join(" ")].join(" ").toLowerCase();
  if (use && hay.includes(use.split(" ")[0])) s += 120;
  (profile.priorities || []).forEach((pr) => { if (hay.includes(pr.toLowerCase())) s += 40; });
  if (profile.price && !profile.price.any) { if (p.price >= profile.price.min && p.price <= profile.price.max) s += 90; else s -= 30; }
  if (p.spotted_certificate && p.spotted_certificate.passed) s += 45;
  s += Math.round((p.rating || 0) * 8);
  if ((p.stock || 0) > 0) s += 20;
  return { p, s, sizeMiss: needSize && !sizes.includes(String(size)) };
}

export default function Concierge() {
  const { profile, setProfile, resetProfile, open, view, setView, openConcierge, close } = useConcierge();
  const loc = useLocation();
  const nav = useNavigate();

  const scrimRef = useRef(null);
  const [inClass, setInClass] = useState(false);
  const [step, setStep] = useState(1);
  const [anim, setAnim] = useState(""); // "", "cc-entering", "cc-leaving"
  const [results, setResults] = useState(null); // {matches, alts, loading, error}
  const autoFired = useRef(false);

  /* first-visit trigger */
  const seen = (() => { try { return localStorage.getItem("ts_concierge_seen") === "1"; } catch { return true; } })();
  useEffect(() => {
    if (autoFired.current || seen || profile.completed) return;
    if (BLOCK_PREFIXES.some((p) => loc.pathname.startsWith(p))) return;
    // NB: set the guard *inside* the timeout, not before it — otherwise React
    // StrictMode's mount→cleanup→remount cancels the first timer and the remount
    // sees the guard already set, so the modal would never open.
    const t = setTimeout(() => { autoFired.current = true; setStep(1); setView("welcome"); openConcierge("welcome"); }, AUTO_OPEN_DELAY);
    return () => clearTimeout(t);
  }, [seen, profile.completed, loc.pathname, openConcierge, setView]);

  /* open/close animation + body scroll lock */
  useEffect(() => {
    if (open) {
      const r1 = requestAnimationFrame(() => requestAnimationFrame(() => setInClass(true)));
      document.body.style.overflow = "hidden";
      return () => cancelAnimationFrame(r1);
    }
    setInClass(false);
    document.body.style.overflow = "";
  }, [open]);

  /* fetch recommendations when entering results */
  useEffect(() => {
    if (!open || view !== "results") return;
    let alive = true;
    setResults({ loading: true });
    const cat = catBySlug(profile.category);
    const params = {};
    if (cat && cat.slug) params.category = cat.slug;
    // Gender is only asked for footwear/apparel; scope results to that department so
    // a Women pick never returns Men listings (backend also folds in Unisex). Falls
    // back to the selected-by-default "men" so results match the visible selection.
    const genderCat = profile.category === "footwear" || profile.category === "apparel";
    if (genderCat) params.department = profile.gender || "men";
    if (profile.price && !profile.price.any) {
      if (profile.price.min > 0) params.min_price = profile.price.min;
      if (profile.price.max !== Infinity) params.max_price = profile.price.max;
    }
    api.get("/products", { params })
      .then(({ data }) => {
        if (!alive) return;
        const pool = (data || []).map((p) => scoreProduct(p, profile));
        const matches = pool.filter((x) => !x.sizeMiss).sort((a, b) => b.s - a.s);
        const alts = pool.filter((x) => x.sizeMiss).sort((a, b) => b.s - a.s);
        setResults({ matches, alts });
      })
      .catch(() => { if (alive) setResults({ error: true, matches: [], alts: [] }); });
    return () => { alive = false; };
  }, [open, view, profile]);

  const goStep = useCallback((n) => {
    setAnim("cc-leaving");
    setTimeout(() => { setStep(n); setAnim("cc-entering"); requestAnimationFrame(() => setAnim("")); }, 190);
  }, []);
  const goView = useCallback((v) => {
    setAnim("cc-leaving");
    setTimeout(() => { setView(v); setAnim("cc-entering"); requestAnimationFrame(() => setAnim("")); }, 190);
  }, [setView]);

  const dismissExplore = () => close();
  const finish = () => { setProfile((p) => ({ ...p, completed: true })); close(); };

  const onScrimClick = (e) => { if (e.target === scrimRef.current) close(); };
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open) return null;

  return (
    <div ref={scrimRef} className={`cc-scrim ${inClass ? "cc-in" : ""}`} onMouseDown={onScrimClick}
      role="dialog" aria-modal="true" aria-label="The Spotters concierge">
      <div className="cc-panel">
        <span className="cc-edge" aria-hidden="true" />
        <button onClick={close}
          className="cc-focus absolute top-4 right-4 z-10 bg-transparent border-0 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground">
          {view === "welcome" ? "Explore instead" : "Explore The Spotters"} ✕
        </button>
        <div className="cc-pad">
          {view === "welcome" && <Welcome onFind={() => { setStep(1); goView("flow"); }} onExplore={dismissExplore} />}
          {view === "flow" && (
            <Flow step={step} anim={anim} profile={profile} setProfile={setProfile}
              onBack={() => step > 1 && goStep(step - 1)}
              onNext={() => { if (step < 5) goStep(step + 1); else { setProfile((p) => ({ ...p, completed: true })); goView("results"); } }}
            />
          )}
          {view === "results" && (
            <Results anim={anim} profile={profile} results={results}
              onProfile={() => goView("profile")}
              onEdit={() => { setStep(1); goView("flow"); }}
              onViewAll={() => { finish(); nav(shopHref(profile)); }}
              onExploreAll={() => { finish(); nav("/shop?picks=1"); }}
              onProduct={(id) => { finish(); nav(`/product/${id}`); }}
            />
          )}
          {view === "profile" && (
            <ProfileView anim={anim} profile={profile}
              onEdit={() => { setStep(1); goView("flow"); }}
              onPicks={() => goView("results")}
              onExploreAll={() => { finish(); nav("/shop?picks=1"); }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function shopHref(profile) {
  const cat = catBySlug(profile.category);
  const p = new URLSearchParams();
  if (cat && cat.slug) p.set("category", cat.slug);
  if (profile.category === "footwear" || profile.category === "apparel") p.set("department", profile.gender || "men");
  if (profile.price && !profile.price.any && profile.price.max !== Infinity) p.set("max_price", String(profile.price.max));
  const qs = p.toString();
  return "/shop" + (qs ? `?${qs}` : "");
}

/* ============================ WELCOME ============================ */
function Welcome({ onFind, onExplore }) {
  return (
    <div className="cc-q">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">The Spotters</p>
      <h2 className="font-serif leading-[0.94] tracking-tighter mt-4" style={{ fontSize: "clamp(38px,7vw,54px)" }}>Find what<br />fits you.</h2>
      <p className="mt-4 text-muted-foreground leading-relaxed max-w-[40ch]">Tell us what you're looking for and we'll narrow it down — a Spotter, quietly guiding you through the marketplace.</p>
      <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.16em] flex items-center gap-3">60 seconds <span className="inline-block w-[5px] h-[5px] bg-[hsl(var(--accent))]" /> No sign-up required</p>
      <div className="mt-7 flex flex-col gap-4 items-start">
        <button onClick={onFind}
          className="cc-focus w-full bg-foreground text-background border-[1.5px] border-foreground px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] flex justify-between items-center hover:bg-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] transition-colors">
          Find my picks <span>→</span>
        </button>
        <button onClick={onExplore}
          className="cc-focus font-mono text-xs uppercase tracking-[0.14em] pb-0.5 border-b-[1.5px] border-foreground/40 hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))]">
          Explore The Spotters →
        </button>
      </div>
      <p className="mt-5 text-[11px] text-muted-foreground">You can change your preferences anytime.</p>
    </div>
  );
}

/* ============================ FLOW ============================ */
function Flow({ step, anim, profile, setProfile, onBack, onNext }) {
  const pad2 = (n) => (n < 10 ? "0" : "") + n;
  const canContinue = step === 1 ? !!profile.category : step === 2 ? !!profile.use : true;

  const togglePriority = (v) => setProfile((p) => {
    const arr = p.priorities || [];
    return { ...p, priorities: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
  });

  const s5 = step5(profile.category || "unsure", profile.gender || "men");

  return (
    <div className={`cc-q ${anim}`}>
      <div className="flex justify-between items-center">
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Let's find your picks</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">{pad2(step)} / 05</span>
      </div>
      <div className="mt-3.5"><div className="cc-rail"><div className="cc-fill" style={{ width: `${((step - 1) / 5) * 100}%` }} /></div></div>

      {step === 1 && (
        <>
          <h3 className="font-serif mt-6 leading-[1.02]" style={{ fontSize: "clamp(26px,4.6vw,34px)" }}>What are you shopping for?</h3>
          <div className="mt-6 grid gap-2.5">
            {CATEGORIES.map((c) => (
              <button key={c.v} onClick={() => setProfile((p) => ({ ...p, category: c.v, use: null, size: null }))}
                className={`cc-opt cc-focus ${profile.category === c.v ? "cc-on" : ""}`}>
                <span className="cc-tick">✓</span>
                <span className="font-serif text-xl leading-none block">{c.t}</span>
                <span className="cc-od block mt-1.5 text-xs text-muted-foreground leading-snug">{c.d}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <h3 className="font-serif mt-6 leading-[1.02]" style={{ fontSize: "clamp(26px,4.6vw,34px)" }}>{USE[profile.category || "unsure"].q}</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {USE[profile.category || "unsure"].opts.map((o) => (
              <button key={o} onClick={() => setProfile((p) => ({ ...p, use: o }))}
                className={`cc-opt cc-focus ${profile.use === o ? "cc-on" : ""}`}>
                <span className="cc-tick">✓</span>
                <span className="font-serif text-xl leading-none block">{o}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <h3 className="font-serif mt-6 leading-[1.02]" style={{ fontSize: "clamp(26px,4.6vw,34px)" }}>What matters most to you?</h3>
          <p className="mt-2 text-xs text-muted-foreground">Choose as many as you like.</p>
          <div className="mt-6 flex flex-wrap gap-2.5">
            {PRIORITIES.map((pr) => (
              <button key={pr} onClick={() => togglePriority(pr)}
                className={`cc-chip cc-focus font-mono text-xs uppercase tracking-[0.14em] ${(profile.priorities || []).includes(pr) ? "cc-on" : ""}`}>{pr}</button>
            ))}
          </div>
        </>
      )}

      {step === 4 && (
        <>
          <h3 className="font-serif mt-6 leading-[1.02]" style={{ fontSize: "clamp(26px,4.6vw,34px)" }}>What's your comfortable price range?</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {PRICES.map((pr, i) => (
              <button key={i} onClick={() => setProfile((p) => ({ ...p, price: pr }))}
                className={`cc-opt cc-focus ${profile.price && profile.price.t === pr.t ? "cc-on" : ""}`}>
                <span className="cc-tick">✓</span>
                <span className="font-serif text-xl leading-none block">{pr.t}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {step === 5 && (
        <>
          <h3 className="font-serif mt-6 leading-[1.02]" style={{ fontSize: "clamp(26px,4.6vw,34px)" }}>{s5.q}</h3>
          {s5.kind === "size" ? (
            <>
              {s5.genders && (
                <div className="mt-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2.5">Who's it for?</p>
                  <div className="flex gap-2">
                    {GENDERS.map((g) => (
                      <button key={g.v} onClick={() => setProfile((p) => ({ ...p, gender: g.v, size: null }))}
                        className={`px-3.5 py-2 font-mono text-[10px] uppercase tracking-widest border transition-colors ${(profile.gender || "men") === g.v ? "bg-foreground text-background border-foreground" : "border-foreground/30 hover:border-foreground"}`}>
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                {s5.opts.map((o) => (
                  <button key={o} onClick={() => setProfile((p) => ({ ...p, size: o }))}
                    className={`cc-sz cc-focus font-serif text-base ${profile.size === o ? "cc-on" : ""}`}>{o}</button>
                ))}
                <button onClick={() => setProfile((p) => ({ ...p, size: null }))}
                  className={`cc-sz cc-wide cc-focus font-mono text-[11px] uppercase tracking-[0.14em] ${!profile.size ? "cc-on" : ""}`}>{s5.skip}</button>
              </div>
              {s5.foot && <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">Tip: shoe sizes vary by brand — the full size guide (with foot length in cm) is on every product page.</p>}
            </>
          ) : (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {s5.opts.map((o) => (
                <button key={o} onClick={() => setProfile((p) => ({ ...p, size: o }))}
                  className={`cc-opt cc-focus ${profile.size === o ? "cc-on" : ""}`}>
                  <span className="cc-tick">✓</span>
                  <span className="font-serif text-xl leading-none block">{o}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      <div className="mt-7 pt-5 border-t-[1.5px] border-foreground/15 flex justify-between items-center">
        <button onClick={onBack} disabled={step === 1}
          className="cc-focus font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground disabled:opacity-30">← Back</button>
        <div className="flex gap-5 items-center">
          <button onClick={onNext} className="cc-focus font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">Skip</button>
          <button onClick={onNext} disabled={!canContinue}
            className="cc-focus bg-foreground text-background border-[1.5px] border-foreground px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] disabled:opacity-35 transition-colors">
            {step === 5 ? "See my picks" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================ RESULTS ============================ */
function summaryPills(profile) {
  const out = [];
  if (profile.use) out.push(profile.use.toUpperCase());
  if (profile.priorities && profile.priorities.length) out.push(profile.priorities.map((x) => x.toUpperCase()).join(" · "));
  if (profile.price) out.push(profile.price.any ? "ALL PRICES" : profile.price.t.toUpperCase());
  if (profile.size) out.push("SIZE " + String(profile.size).toUpperCase());
  return out;
}

function whyReasons(p, profile, alt) {
  const sizes = (p.sizes || []).map(String);
  const bits = [];
  if (profile.size) {
    if (sizes.includes(String(profile.size))) bits.push(`available in <b>size ${profile.size}</b>`);
    else if (alt) bits.push(`close fit — <b>size ${profile.size}</b> not in stock`);
  }
  if (profile.price && !profile.price.any && p.price >= profile.price.min && p.price <= profile.price.max) bits.push(`within <b>${profile.price.t}</b>`);
  if (p.spotted_certificate && p.spotted_certificate.passed) bits.push("physically <b>Spotter Certified</b>");
  if (!bits.length) bits.push("inspected before it ships");
  const base = "You chose " + (profile.use ? profile.use.toLowerCase() : "quality")
    + (profile.priorities && profile.priorities.length ? " and " + profile.priorities.join(" & ").toLowerCase() : "");
  return `${base} — this ${bits.slice(0, 3).join(", ")}.`;
}

function ProductCard({ x, best, alt, profile, onProduct }) {
  const p = x.p;
  const sizes = (p.sizes || []).map(String);
  const certified = p.spotted_certificate && p.spotted_certificate.passed;
  const sizeMatch = profile.size && sizes.includes(String(profile.size));
  const inStock = (p.stock || 0) > 0;
  return (
    <button onClick={() => onProduct(p.id)} className="cc-focus text-left border-[1.5px] border-foreground grid grid-cols-[110px_1fr] w-full hover:bg-foreground/[0.02]">
      <div className="bg-foreground/[0.05] border-r-[1.5px] border-foreground/15 flex items-center justify-center overflow-hidden">
        {p.images && p.images[0]
          ? <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" style={{ minHeight: 120 }} />
          : <span className="text-4xl">🛍️</span>}
      </div>
      <div className="p-4">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {best && <span className="font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-1 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border border-[hsl(var(--accent))]">Best match</span>}
          {certified
            ? <span className="font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-1 bg-foreground text-background border border-foreground">Spotter Certified</span>
            : <span className="font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-1 border border-foreground">Inspected</span>}
          {sizeMatch && <span className="font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-1 border border-foreground">BIS Size Match</span>}
        </div>
        <div className="font-serif text-lg leading-none">{p.title}</div>
        <div className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground mt-1">{p.brand || p.vendor_name}{sizes.length ? ` · Sizes ${sizes[0]}–${sizes[sizes.length - 1]}` : ""}</div>
        <div className="mt-2 flex justify-between items-baseline gap-2.5">
          <span className="font-mono text-[15px]">{inr(p.price)}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{inStock ? "In stock" : "Restocking"}{p.rating ? ` · ★ ${Number(p.rating).toFixed(1)}` : ""}</span>
        </div>
        <div className="cc-why mt-3 pt-2.5">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Why this made your picks</div>
          <div className="text-xs leading-snug mt-1" dangerouslySetInnerHTML={{ __html: whyReasons(p, profile, alt) }} />
        </div>
      </div>
    </button>
  );
}

function Results({ anim, profile, results, onProfile, onViewAll, onExploreAll, onProduct }) {
  const pills = summaryPills(profile);
  if (!results || results.loading) {
    return <div className={`cc-q ${anim}`}><p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Your Spotter Picks</p><p className="font-serif text-3xl mt-4">Finding your picks…</p></div>;
  }
  const matches = results.matches || [];
  const alts = results.alts || [];
  const shown = matches.slice(0, 6);
  const sizeMatched = profile.size ? matches.filter((x) => (x.p.sizes || []).map(String).includes(String(profile.size))).length : null;
  const available = matches.filter((x) => (x.p.stock || 0) > 0).length;
  const certified = matches.filter((x) => x.p.spotted_certificate && x.p.spotted_certificate.passed).length;
  const noExact = profile.size && sizeMatched === 0;

  return (
    <div className={`cc-q ${anim}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Your Spotter Picks</p>
      <h2 className="font-serif tracking-tighter leading-[0.94] mt-3" style={{ fontSize: "clamp(34px,6vw,46px)" }}>Based on<br />what you told us.</h2>
      {pills.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2.5">
          {pills.map((p, i) => <span key={i} className="border-[1.5px] border-foreground px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em]">{p}</span>)}
        </div>
      )}
      <p className="mt-6 font-serif text-3xl">We found {matches.length} pick{matches.length === 1 ? "" : "s"} for you.</p>

      <div className="mt-4 grid grid-cols-3 border-[1.5px] border-foreground">
        {sizeMatched !== null && <Metric n={sizeMatched} label="Match your size" />}
        <Metric n={available} label="Available now" />
        <Metric n={certified} label="Spotter Certified" last />
      </div>

      {noExact && <p className="mt-3.5 text-xs text-muted-foreground">No exact match in size {profile.size} right now — here are the closest picks, with alternatives below.</p>}

      {matches.length === 0 && !noExact && (
        <p className="mt-5 font-serif text-xl text-muted-foreground">Nothing matches those preferences yet — try widening them, or explore everything below.</p>
      )}

      <div className="mt-5 flex flex-col gap-3.5">
        {shown.map((x, i) => <ProductCard key={x.p.id} x={x} best={i === 0} profile={profile} onProduct={onProduct} />)}
      </div>

      {noExact && alts.length > 0 && (
        <>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Close alternatives · other sizes</p>
          <div className="mt-3 flex flex-col gap-3.5">
            {alts.slice(0, 2).map((x) => <ProductCard key={x.p.id} x={x} alt profile={profile} onProduct={onProduct} />)}
          </div>
        </>
      )}

      <div className="mt-6 flex flex-col gap-3">
        {matches.length > 0 && (
          <button onClick={onViewAll} className="cc-focus w-full bg-foreground text-background border-[1.5px] border-foreground px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] flex justify-between items-center hover:bg-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] transition-colors">
            View all {matches.length} picks <span>→</span>
          </button>
        )}
        <button onClick={onProfile} className="cc-focus font-mono text-xs uppercase tracking-[0.14em] pb-0.5 border-b-[1.5px] border-foreground/40 self-start hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))]">See / edit my shopping profile →</button>
        <button onClick={onExploreAll} className="cc-focus font-mono text-xs uppercase tracking-[0.14em] pb-0.5 border-b-[1.5px] border-foreground/40 self-start hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))]">See all my picks →</button>
      </div>
    </div>
  );
}

function Metric({ n, label, last }) {
  return (
    <div className={`p-4 ${last ? "" : "border-r-[1.5px] border-foreground/15"}`}>
      <b className="font-serif text-[28px] block leading-none">{n}</b>
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
    </div>
  );
}

/* ============================ PROFILE / MY PICKS ============================ */
function ProfileView({ anim, profile, onEdit, onPicks, onExploreAll }) {
  const cat = catBySlug(profile.category);
  const rows = [
    ["Category", cat ? cat.t : "—"],
    ["Use", profile.use || "—"],
    ["Preferences", profile.priorities && profile.priorities.length ? profile.priorities.join(" · ") : "—"],
    ["Price range", profile.price ? (profile.price.any ? "All prices" : profile.price.t) : "—"],
    ["Size", profile.size || "Not set"],
  ];
  return (
    <div className={`cc-q ${anim}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">My Picks</p>
      <h2 className="font-serif tracking-tighter leading-[0.94] mt-3" style={{ fontSize: "clamp(32px,6vw,44px)" }}>Your shopping<br />profile.</h2>
      <div className="mt-6 border-[1.5px] border-foreground">
        {rows.map((r, i) => (
          <div key={r[0]} className={`grid grid-cols-[130px_1fr] ${i < rows.length - 1 ? "border-b-[1.5px] border-foreground/15" : ""}`}>
            <div className="p-3.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-r-[1.5px] border-foreground/15 flex items-center">{r[0]}</div>
            <div className="p-3.5 font-serif text-lg">{r[1]}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-col gap-3">
        <button onClick={onEdit} className="cc-focus w-full bg-foreground text-background border-[1.5px] border-foreground px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] flex justify-between items-center hover:bg-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] transition-colors">
          Edit preferences <span>→</span>
        </button>
        <button onClick={onPicks} className="cc-focus font-mono text-xs uppercase tracking-[0.14em] pb-0.5 border-b-[1.5px] border-foreground/40 self-start hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))]">Back to my picks →</button>
        <button onClick={onExploreAll} className="cc-focus font-mono text-xs uppercase tracking-[0.14em] pb-0.5 border-b-[1.5px] border-foreground/40 self-start hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))]">See all my picks →</button>
      </div>
    </div>
  );
}
